import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { useQuery } from "@tanstack/react-query";
import { api, type AgentModelCatalog } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";

/** The model catalog (`catalog` when the caller holds it) and a display name per id. */
export function useClaudeModels(tenantId?: string | null, catalog?: AgentModelCatalog) {
  const query = useQuery({
    queryKey: keys.agentDefinition.models(tenantId ?? null),
    queryFn: () => api.getAgentModels(tenantId),
    staleTime: 10 * 60 * 1000,
    enabled: !catalog,
  });
  const data = catalog ?? query.data;
  const models = data?.models ?? [];
  const nameOf = (id: string) => models.find((m) => m.id === id)?.display_name ?? id;
  return { data, models, nameOf, isLoading: !catalog && query.isLoading };
}

/**
 * The Claude model an agent runs on; blank = the default (the newest Sonnet,
 * decided by the backend: `agents/models.py`). Forms show it inside
 * `AdvancedModelFold`, never on its own.
 *
 * With `tenantId` the list is what the organization paying for that tenant's
 * runs can use. `inherited` is what blank falls through to before the
 * newest Sonnet (an agent's own model, for a workflow step). A caller
 * that already holds the list (it comes with the "Run agent" plan, readable
 * without workflow access) passes it as `catalog`.
 */
export default function ClaudeModelSelect({
  tenantId,
  value,
  onChange,
  inherited,
  defaultLabel = "Default",
  disabled,
  width,
  catalog,
}: {
  tenantId?: string | null;
  value: string;
  onChange: (model: string) => void;
  inherited?: string | null;
  defaultLabel?: string;
  disabled?: boolean;
  width?: number;
  catalog?: AgentModelCatalog;
}) {
  const { data, models, nameOf, isLoading } = useClaudeModels(tenantId, catalog);
  const fallsTo = inherited || data?.default_model;
  const defaultText = fallsTo ? `${defaultLabel} (${nameOf(fallsTo)})` : defaultLabel;

  return (
    <TextField
      label="Model"
      select
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      fullWidth={width === undefined}
      sx={width === undefined ? undefined : { width }}
      helperText={
        data?.source === "fallback"
          ? "Couldn't list models from Anthropic — showing a standard list."
          : undefined
      }
      slotProps={{
        select: {
          renderValue: (v) => (v ? nameOf(v as string) : defaultText),
          displayEmpty: true,
        },
        inputLabel: { shrink: true },
      }}
    >
      <MenuItem value="">
        <ListItemText
          primary={defaultLabel}
          secondary={fallsTo ? nameOf(fallsTo) : isLoading ? "Loading…" : undefined}
        />
      </MenuItem>
      {models.map((m) => (
        <MenuItem key={m.id} value={m.id}>
          <ListItemText primary={m.display_name} secondary={m.hint ? `${m.hint} · ${m.id}` : m.id} />
        </MenuItem>
      ))}
      {/* A saved model that isn't listed (an alias, or a fallback list) never shows blank. */}
      {value && !models.some((m) => m.id === value) && (
        <MenuItem value={value}>
          <ListItemText primary={value} secondary="Not in the list" />
        </MenuItem>
      )}
    </TextField>
  );
}
